/**
 * PaperItemsEditor — 시험지 정의 편집(제목·설명·문항, 계약 §2·§5-1).
 *
 * items 의 배열 순서가 곧 출제 순서(order_no)다 — 화면의 위아래가 그대로 시험지 순서라는 뜻이라,
 * 순서 조작을 별도 개념으로 감추지 않고 각 행에 올리기/내리기를 둔다. 드래그는 좁은 패널·모바일에서
 * 실패하기 쉬워 보조로만 쓴다.
 *
 * 저장은 PATCH 한 번에 제목·설명·문항을 함께 보낸다(계약상 변경 시 revision 이 오른다).
 * 새 시험지는 POST — 저장 전까지 서버에 아무것도 없다는 것을 화면에 분명히 적는다.
 */
import React, { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import { Plus, Trash2, RotateCcw, Save, Eye } from 'lucide-react';
// 개념 설명 창은 저장소 공용 부품이다(문제 미리보기·AI트래커·OCR 검수가 같은 것을 쓴다).
// **파일을 직접 lazy 로 부르지 않는다** — 새 청크가 서면 그 안에서 common/index 가 다시 평가되고,
// 거기 딸려 오는 AiRunModal.jsx:23 의 잘못된 default import 가 빌드를 깬다(Modal.js 는 named 뿐).
// conceptIds 를 주면 모달이 스스로 v1 을 치므로 **주지 않는다** — 여기서 v2 로 받아 data 로 넘긴다.
import { Button, MathceptDetailModal } from '../common';
import { useAuth } from '../../context/AuthContext';
import { createPaper, updatePaper, deletePaper, isDeletedItem, describeApiError, AUTH_REDIRECT } from './examPaperApi';
import { fetchConceptDictV2, fetchConceptDetailV2 } from './conceptBadge';
// 문항 메타(개념·난이도·라벨·need_*)를 **본문 없이** 묶어 받는 이음매. 개념 배지만 채우던
// 종전 호출을 넓힌 것이라 왕복이 늘지 않는다 — with_body 스위치로 본문 트랙과 갈린다.
import { fetchProblemItemsV2 } from './problemBodyV2';
import { buildPaperStats, conceptPairsOf } from './paperStats';
import PaperStatsPanel from './PaperStatsPanel';
// 눈에 든 행의 본문만 20개씩 받아 두는 훅(오너 요구의 「최소한의 부분 요청」)
import useRowBodies, { findScrollParent } from './useRowBodies';
import ProblemPickerModal from './ProblemPickerModal';
import PaperItemPreviewRow, { PAPER_ITEM_ROW_MAX_PX } from './PaperItemPreviewRow';
// 폭 단계(한 칸 · 두 칸 · 카드 2열 · 넓음)를 정하는 자 — 문턱과 그 셈은 거기 한 곳에 있다
import { itemsLayout } from './paperItemsLayout';

// 행에 남는 건 번호와 평문 요약뿐이라 무엇을 담았는지 알 수 없다 — 실제 지면은 이 창이 그린다
// v2 전용 미리보기(오너 지시 2026-08-26). 구형 ProblemPreview 는 손대지 않는다 —
// 그쪽은 v1 셋(/api/problems/print · /api/mathcepts/temp · …/by-prob-ids)에 묶여 있고
// 다른 화면 여럿이 그대로 쓴다.
const ProblemPreviewV2 = lazy(() => import('./ProblemPreviewV2'));

// 한 번에 띄우는 상한. 구형은 print/page 를 per_page=50 으로 한 번만 쳐서 생긴 값이고,
// v2 는 문제당 요청이라 그 제약이 없지만 — 50개를 한 화면에 그리는 것 자체가 무겁다.
const PREVIEW_MAX = 50;

/* ═══════════════════════════════════════════════════════════════════════════
 * 2열 — 좌(통계) / 우(문항 목록). 임계를 **뷰포트가 아니라 이 패널의 폭**으로 잰다.
 *
 * 뷰포트로 재면 틀린다. 이 패널이 갖는 폭은 `뷰포트 − 480px`(사이드바 펼침) 또는
 * `− 390px`(접힘)이라, 같은 1280px 화면에서도 사이드바 상태에 따라 890 과 980 으로 갈린다.
 * `xl:` 같은 분기점은 사이드바가 접힌 것을 모른다 — 재면 저절로 따라온다.
 * ═══════════════════════════════════════════════════════════════════════════ */
/**
 * **고정하는 쪽은 오른쪽이다.** 처음에는 좌측 통계를 300px 로 못 박고 목록에 남는 폭을 줬는데,
 * 목록의 최대 폭이 본문 한 단(485)이라 그 위로 남는 폭이 전부 **빈 자리로 남았다**
 * (오너 지적 2026-08-28 「우측에 여백이 너무 많이 남음」).
 *
 * 그래서 목록을 485 로 고정하고 통계가 남는 폭을 전부 갖게 뒤집었다. 통계는 넓어질수록
 * 나빠지지 않는다 — 막대가 길어지고 개념 이름이 더 보인다. 목록은 넓어져도 얻는 것이 없다:
 * 본문은 지면 폭에서 **확대하지 않기 때문에**(종이보다 커 보이면 안 된다) 남는 폭이 여백이 된다.
 * (두 열 사이 간격은 격자의 gap-4 가 갖는다.)
 */
/**
 * 행이 본문 앞뒤로 먹는 폭 —
 * li 테두리 2 + 안쪽 여백 16 + 번호 36 + gap 8 + **본문 카드**(테두리 2 + 안쪽 여백 20) = **84**.
 *
 * 순서·삭제 버튼단(22)이 여기 있었는데 걷어 냈고(오너 지시 2026-08-28), 대신 본문이 카드로
 * 감싸이며(같은 날 지시 「문제 부분만 카드형태로, 2xl 라운드」) 그 여백이 들어왔다.
 */
const ROW_CHROME_PX = 84;
/**
 * 본문을 그리는 하한. ProblemBodyPreview 는 지면 폭 423px 로 그린 뒤 `칸/423` 배로 줄이고
 * 지면 글자가 18px 이므로, 화면 글자는 `18 × min(1, 칸/423)` 이다. 이것이 **12px**(이 저장소
 * 글자 6단의 하한) 아래로 내려가면 본문이 정보가 아니라 얼룩이 된다 → 칸 282px. 6px 여유.
 */
const BODY_MIN_PX = 288;
// 2열 하한(770)과 그 실측 근거는 paperItemsLayout.js 로 옮겼다(2026-09-15) — 카드 2열·통계 나눔 문턱이
// 더해져 셋이 되었고, 셋이 서로의 셈(1,262 = 978 + 16 + 268)으로 묶여 있어 한 곳에서 보여야 한다.

/**
 * 목록 한 줄의 최대 폭(오너 지시 2026-08-28 「미리보기 폭을 현재 미리보기의 가로 크기에 맞게」).
 *
 * 본문은 지면 한 단 폭 **423px** 로 그린 뒤 `min(1, 칸/423)` 배로 **줄이기만 한다** — 칸이 넓어도
 * 확대하지 않는다(종이보다 커 보이면 안 된다). 그래서 칸이 423 을 넘으면 그 넘은 만큼이 그대로
 * 빈 자리로 남았다. 행을 본문 폭에 맞춰 세우면 그 빈 자리가 사라지고, 목록이 실제 지면 한 단과
 * 같은 너비로 선다. 423 + 크롬 62 = **485**.
 */
const ROW_MAX_PX = PAPER_ITEM_ROW_MAX_PX;

/**
 * 상세의 문항 배열 — 계약 §2가 "현재 문항"이라고만 하고 키를 못박지 않았다. 형제 응답
 * (배포본)이 다섯 가지 이름을 쓰는 걸 보면 한 이름으로 단정하는 건 위험하다.
 * 못 알아보면 null 을 돌려준다 — 빈 배열로 넘기면 화면이 "문항 없음"으로 보이고,
 * 첫 저장이 기존 문항을 통째로 지운 PATCH 가 된다(revision 도 올라 정상 편집처럼 보인다).
 */
function pickItems(p) {
  if (p == null) return [];
  if (Array.isArray(p.items)) return p.items;
  if (Array.isArray(p.problems)) return p.problems;
  if (Array.isArray(p.snapshot?.items)) return p.snapshot.items;
  return null;
}


/**
 * 서버 문항 → 편집 행. points 는 선택이라 없으면 빈 문자열(0과 구분).
 * isDeleted 는 계약 §1-3 의 items[].is_deleted 다 — 원본 문제가 지워진 문항이고,
 * 이것이 하나라도 남아 있으면 배포·복사·되돌리기가 409(deleted_items)로 막힌다.
 */
function toRows(items) {
  if (!Array.isArray(items)) return [];
  return items.map((it) => ({
    problem_id: it.problem_id ?? it.id,
    points: it.points == null ? '' : String(it.points),
    preview: it.preview ?? it.problem_preview ?? '',
    difficulty: it.difficulty ?? null,
    labels: it.labels ?? '',
    isDeleted: isDeletedItem(it),
    // 상세가 개념을 실어 주면 그대로 쓴다 — 계약이 못박지 않아 이름을 넓게 받는다.
    // 비면 problem-search 로 한 번에 보충한다(아래 useEffect).
    concepts: conceptPairsOf(it),
  }));
}

/**
 * 편집 행 → 요청 items. 배점을 비워 두면 키 자체를 보내지 않는다(0점이 아니다).
 *
 * **배점은 저장만 되고 채점에 쓰이지 않는다**(계약 §10, 오너 결정 2026-08-26). 이 화면은 배점을
 * 입력받지도 보여 주지도 않고, 서버가 준 값을 그대로 되돌려 보내 잃지 않게만 한다.
 * (계약 §10-3 의 「FE 미사용」은 **「백엔드 미사용(기록만)」의 오기**다 — BE-20260829-reply-fe-0828.md
 *  §1 3-2 가 정정했다. FE 는 지금처럼 보내고 읽는 것이 맞고, 백엔드가 채점·합계에 쓰지 않는다.)
 * 채점 연동이 생기기 전에 화면이 배점을 「점수」처럼 말하면 거짓이 된다 — 문구를 붙이지 마라.
 */
function toPayload(rows) {
  return rows.map((r) => {
    const o = { problem_id: Number(r.problem_id) };
    if (String(r.points).trim() !== '') o.points = Number(r.points);
    return o;
  });
}

/**
 * 보내기 직전 중복 검사(계약 §1-3) — 같은 problem_id 가 두 번 들어가면 POST/PATCH 가 **400** 이다.
 * 담기 경로가 이미 막고 있지만(addProblems 의 have 집합) 그쪽은 String 으로 견주고 요청은 Number 로
 * 나간다 — '12' 와 12 가 섞이면 화면에서는 다른 행인데 서버에는 같은 값 둘이 된다.
 * 그래서 **나가는 값 그대로** 한 번 더 센다. 돌려주는 것은 두 번 이상 나온 problem_id 배열.
 */
function duplicateIds(items) {
  const seen = new Set();
  const dup = new Set();
  items.forEach((o) => {
    if (seen.has(o.problem_id)) dup.add(o.problem_id);
    else seen.add(o.problem_id);
  });
  return Array.from(dup);
}

function PaperItemsEditor({ paper, isNew = false, onSaved, onDeleted, onDirtyChange = null, onOpenCompose = null }) {
  const { getAccessToken } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null); // 'save' | 'delete'
  const [toast, setToast] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  /**
   * problem_id → 검색 응답 item. **값이 null 이면 「물어봤는데 못 받았다」**이고,
   * 키 자체가 없으면 「아직 안 물어봤다」다. 좌측 통계가 이 둘을 갈라 모수를 밝힌다.
   *
   * rows 가 아니라 여기 두는 것이 요점이다 — seed() 가 rows 를 통째로 갈아 끼우므로
   * (되돌리기 한 번, 저장 후 판 상승 한 번) 행에 실어 두면 받아 둔 것이 전부 날아가고
   * 같은 왕복이 다시 난다. problem_id 로 결정되는 값이라 행이 바뀌어도 유효하다.
   */
  const [metaMap, setMetaMap] = useState({});
  // concept_id → {order, concept_type}. 배지의 순번·유형은 이 사전에서만 나온다.
  const [conceptDict, setConceptDict] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmRevert, setConfirmRevert] = useState(false); // 한 번에 편집 전부를 버리는 조작이라
  // 미리보기로 띄운 문항 — 순번을 함께 얼려 둔다(연 뒤에 순서를 바꿔도 보던 화면이 흔들리지 않게)
  const [preview, setPreview] = useState(null); // { ids: [], numbers: [] } | null
  const seedRef = useRef({ title: '', description: '', rows: [] });
  /**
   * StrictMode 는 마운트 → cleanup → 마운트를 돌린다. **마운트마다 되살리지 않으면** dev 에서
   * false 로 굳어, 응답이 와도 상태를 못 갈고 통계가 영영 비어 있다(형제 화면이 같은 주석으로
   * 같은 함정을 막아 두었다 — ProblemPreviewV2·AssignPanel).
   */
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  /**
   * 이 패널의 실제 폭 — 2열로 설지, 본문을 그릴지를 여기서 정한다.
   * 뷰포트 분기점(`xl:` 등)을 쓰지 않는 이유는 상수 주석에 적어 두었다(사이드바가 접히면
   * 같은 화면 폭에서도 이 패널의 폭이 90px 달라진다).
   */
  const rootRef = useRef(null);
  const [panelW, setPanelW] = useState(0);
  useEffect(() => {
    const el = rootRef.current;
    if (el == null || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => {
      const w = el.clientWidth;
      setPanelW((prev) => (prev === w ? prev : w));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /**
   * 좌측 열의 높이 — **스크롤을 맡은 조상의 높이**다(오너 지시 2026-08-28
   * 「좌측열은 스크롤없이, 해당 영역에 꽉 차도록」).
   *
   * `100vh` 로 어림하지 않는다. 이 패널 위에는 헤더·탭줄·목록 머리줄이 있고 그 높이가
   * 상황마다 다르다(경고 띠가 뜨면 또 달라진다) — 어림하면 어떤 상태에서는 넘치고 어떤
   * 상태에서는 남는다. 스크롤 컨테이너를 직접 재면 그 전부가 이미 반영돼 있다.
   */
  const [colH, setColH] = useState(0);
  useEffect(() => {
    const el = rootRef.current;
    if (el == null || typeof ResizeObserver === 'undefined') return undefined;
    const sp = findScrollParent(el);
    if (sp == null) return undefined;
    const measure = () => {
      const h = sp.clientHeight;
      setColH((prev) => (prev === h ? prev : h));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(sp);
    return () => ro.disconnect();
  }, []);
  // 서버 문항을 못 알아본 상태 — 이때는 items 를 아예 보내지 않는다(지우지 않기 위해)
  const [itemsUnknown, setItemsUnknown] = useState(false);

  const seed = useCallback((p) => {
    const t = p?.title ?? '';
    const d = p?.description ?? '';
    const picked = pickItems(p);
    setItemsUnknown(p != null && picked === null);
    const r = toRows(picked);
    setTitle(t);
    setDescription(d);
    setRows(r);
    setConfirmDelete(false);
    setConfirmRevert(false);
    setPreview(null); // 다른 판을 읽었는데 옛 문항이 떠 있으면 지금 목록이라 오해한다
    seedRef.current = { title: t, description: d, rows: r };
  }, []);

  // 같은 판을 다시 읽었을 뿐인데 seed 를 다시 돌리면 미저장 편집이 조용히 사라진다.
  // (형제 패널이 목록을 갱신할 때마다 paper 객체 identity 가 바뀐다.)
  const seededKey = useRef(null);
  useEffect(() => {
    const key = paper == null ? 'new' : `${paper.paper_id ?? paper.id}-${paper.revision ?? 0}`;
    if (seededKey.current === key) return;
    seededKey.current = key;
    seed(paper);
    setToast(null);
  }, [paper, seed]);

  /**
   * 계약 §1-3 — 원본 문제가 삭제된 문항 수. **편집 중인 rows 를 센다**(서버 최상위
   * deleted_item_count 가 아니라) — 사용자가 그 행을 빼면 경고도 함께 사라져야 하고,
   * 저장 전에는 서버 값이 아직 옛 판이기 때문이다.
   */
  const deletedCount = rows.filter((r) => r.isDeleted).length;

  const dirty = title !== seedRef.current.title
    || description !== seedRef.current.description
    || JSON.stringify(toPayload(rows)) !== JSON.stringify(toPayload(seedRef.current.rows));

  // 부모(탭·목록)가 이동 전에 물을 수 있도록 알린다
  const dirtyCb = useRef(onDirtyChange);
  dirtyCb.current = onDirtyChange;
  // 의존성 배열 없이 매 렌더 통지한다 — 부모가 가드를 통과시키며 플래그를 껐을 때도 다시 켜져야
  // 한다(값이 안 바뀌면 [dirty] 판은 다시 알리지 않는다).
  useEffect(() => { dirtyCb.current?.(dirty); });
  useEffect(() => () => dirtyCb.current?.(false), []);

  // ProblemPreviewV2 는 공용 Modal 이라 ESC 를 스스로 받는다. 이 리스너는 그보다 앞서
  // 같은 키를 먹으므로 남겨 둔다 — 둘 다 같은 곳(setPreview(null))으로 가서 충돌하지 않는다.
  useEffect(() => {
    if (preview == null) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setPreview(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [preview]);

  // 순서 바꾸기·행 빼기는 이 화면에 없다(오너 지시 2026-08-28). 그 조작은 조립 창이 맡는다 —
  // [시험지 편집]으로 넘어가면 지면을 보며 끌어 옮기고 뺄 수 있다.

  /** 담기 — 고른 화면이 보여 주던 미리보기·난이도·라벨을 그대로 들고 온다(저장 전에도 읽히게) */
  const addProblems = (picked) => {
    const have = new Set(rows.map((r) => String(r.problem_id)));
    const added = picked
      .map((p) => (typeof p === 'object' ? p : { problem_id: p }))
      .filter((p) => !have.has(String(p.problem_id)))
      .map((p) => ({
        problem_id: p.problem_id,
        points: '',
        preview: p.preview ?? '',
        difficulty: p.difficulty ?? null,
        labels: p.labels ?? '',
      }));
    if (added.length === 0) {
      setToast({ type: 'info', text: '고른 문제가 모두 이미 담겨 있습니다.' });
      return;
    }
    setRows([...rows, ...added]);
    setToast({ type: 'info', text: `${added.length}개를 뒤에 담았습니다 — 저장해야 반영됩니다.` });
  };

  /**
   * 개념 사전 — 순번(order)·유형(concept_type)은 **개념 자체의 속성**이라 문제 응답에 없고
   * 이 사전에만 있다. 그 두 칸이 없으면 배지는 이름만 남는다.
   *
   * **2026-08-26 로 v1(GET /api/mathcepts/temp) → v2(GET /api/v2/mathcepts) 로 갈아탔다**(계약 §8).
   * 예고해 둔 그 교체다 — 이 화면은 v2 전용이라 v1 을 하나라도 물고 있으면 그 말이 거짓이 된다.
   * v2 는 권한이 user_required 라 교사 토큰으로도 열리고(배지 사전에는 PII 가 없다),
   * order 는 교육과정의 전역 정렬 순번(유니크)이라 화면에 그대로 찍어도 된다(계약 §8).
   * 모듈 캐시(5분)는 conceptBadge.jsx 안에 있어 화면 여러 곳이 같은 사전을 나눠 쓴다.
   * 실패하면 null 을 돌려주고 조용히 넘어간다 — 그때는 이름만 있는 배지가 된다.
   */
  useEffect(() => {
    let alive = true;
    Promise.resolve(fetchConceptDictV2(getAccessToken))
      .then((list) => { if (alive) setConceptDict(Array.isArray(list) ? list : []); })
      .catch(() => { if (alive) setConceptDict([]); });
    return () => { alive = false; };
  }, [getAccessToken]);

  /**
   * ── 메타 트랙 — 전 문항, **본문 없이** ────────────────────────────────────
   * 종전에는 「개념이 비어 있는 행」만 물었다. 이제 좌측 통계가 난이도·need_* 까지 세야 해서
   * **모든 행**을 묻는다. 왕복은 늘지 않는다 — 같은 엔드포인트에 problem_ids 를 콤마로 실어
   * 100개씩 끊어 보내므로 200문항이면 2회다(종전에도 개념 없는 행이 대부분이면 비슷했다).
   *
   * `with_body` 를 **얹지 않는 것**이 이 트랙의 핵심이다. 통계는 전량이 필요하고 본문은 눈에 든
   * 것만 필요하다. 합치면 시험지를 여는 순간 본문 200벌이 딸려 온다 — 오너가 말한 그 「부하」다.
   *
   * 자국(metaSeenRef)은 **요청을 보내기 전에** 남긴다. 응답을 기다리는 사이 [위로]를 누르면
   * rows 가 새 배열이 되어 이 effect 가 다시 도는데, 자국이 없으면 같은 요청이 한 번 더 난다.
   * 물어본 id 는 **응답에 없어도 키를 채운다**(값 null) — 안 채우면 렌더마다 다시 묻는다.
   */
  const metaSeenRef = useRef(new Set());
  const metaAskKey = rows
    .map((r) => (r.problem_id == null ? '' : String(r.problem_id)))
    .filter((id) => id !== '' && !metaSeenRef.current.has(id))
    .join(',');
  useEffect(() => {
    if (metaAskKey === '') return;
    // StrictMode 의 두 번째 호출까지 막으려면 자국은 effect 안에서 다시 걸러야 한다
    const ids = metaAskKey.split(',').filter((id) => !metaSeenRef.current.has(id));
    if (ids.length === 0) return;
    ids.forEach((id) => metaSeenRef.current.add(id));
    (async () => {
      // 전부 실패하면 null 이 온다(던지지 않는다) — 그때도 키를 null 로 채워 두고,
      // 좌측 패널의 [다시 시도]가 자국을 지워 다시 묻는다.
      const got = await fetchProblemItemsV2(ids, getAccessToken);
      if (!aliveRef.current) return;
      const add = {};
      ids.forEach((id) => { add[id] = null; });
      if (got) {
        Object.entries(got).forEach(([k, it]) => { if (k in add) add[k] = it; });
      }
      setMetaMap((prev) => ({ ...prev, ...add }));
    })();
  }, [metaAskKey, getAccessToken]);

  /**
   * 개념 설명 창 — 배지를 누르면 그 개념 하나를 v2 로 받아 공용 모달에 넘긴다
   * (오너 지시 2026-08-28 「모든 매스컨셉 배지를 선택하면 매스컨셉 설명 모달이 실행되도록」).
   *
   * 사전(conceptDict)을 그대로 넘기지 않는 이유는 **설명이 그 안에 없기 때문**이다 —
   * 717개 전부에 설명을 실으면 캐시가 무거워져 계약이 `with_description=1` 스위치로 갈라 놓았다.
   * 그래서 누른 그때 한 번 받는다. `null` 이면 창이 닫힌 상태다.
   */
  const [conceptDetail, setConceptDetail] = useState(null); // { loading, data }
  const openConcept = useCallback(async (conceptId) => {
    if (conceptId == null) return;
    setConceptDetail({ loading: true, data: [] });
    const list = await fetchConceptDetailV2([conceptId], getAccessToken);
    if (!aliveRef.current) return;
    // 실패는 던지지 않는다(이음매 규약). **빈 창을 띄우지 않는다** — 못 받았는데 창만 열리면
    // 사용자는 「설명이 없는 개념」으로 읽는다. 창을 닫고 못 받았다고 말하는 편이 정직하다.
    if (list == null) {
      setConceptDetail(null);
      setToast({ type: 'error', text: '개념 설명을 불러오지 못했습니다.' });
      return;
    }
    setConceptDetail({ loading: false, data: list });
  }, [getAccessToken]);

  /** 못 받은 문항만 다시 — 자국과 지도에서 함께 지우면 위 effect 가 그 id 들을 새로 묻는다 */
  const retryMeta = useCallback(() => {
    const failed = Object.keys(metaMap).filter((k) => metaMap[k] == null);
    if (failed.length === 0) return;
    failed.forEach((id) => metaSeenRef.current.delete(id));
    setMetaMap((prev) => {
      const next = { ...prev };
      failed.forEach((id) => { delete next[id]; });
      return next;
    });
  }, [metaMap]);

  /**
   * 개념 사전 색인 — `matchConcept` 은 717개를 `find` 로 훑고 비교마다 `String()` 을 만든다.
   * 행 200개 × 개념 4개면 한 번 그릴 때만 80만 번 비교다. 여기서는 Map 하나로 한 번에 찾는다.
   * (공용 헬퍼를 고치지 않는 것은 그것을 쓰는 다른 화면의 동작을 바꾸지 않기 위해서다 —
   * 찾는 규칙은 같다: id 형이 응답마다 갈려 문자열로 견준다.)
   */
  const dictIndex = useMemo(() => {
    if (!conceptDict?.length) return null;
    const m = new Map();
    conceptDict.forEach((c) => { if (c?.id != null) m.set(String(c.id), c); });
    return m;
  }, [conceptDict]);

  const conceptsOf = useCallback((r) => {
    const meta = r.problem_id == null ? null : metaMap[String(r.problem_id)];
    const pairs = r.concepts?.length ? r.concepts : (meta ? conceptPairsOf(meta) : []);
    // 사전이 아직·영영 없으면 이름만 있는 배지가 된다(구형과 같은 조건부 구조)
    if (dictIndex == null) return pairs.filter((c) => c.name).map((c) => ({ ...c, order: null, type: null }));
    return pairs.map((c) => {
      const m = c.id == null ? null : dictIndex.get(String(c.id));
      return {
        ...c,
        name: c.name || m?.concept_name || m?.name || '',
        order: m?.order ?? null,
        type: m?.concept_type ?? null,
      };
    }).filter((c) => c.name);
  }, [metaMap, dictIndex]);

  // 폭 단계 — 두 칸인지 · 카드가 몇 열인지 · 통계를 나눌지 · 목록 칸 폭. 카드 2열이 통계 나눔보다 먼저다
  const { twoCol, listCols, statsSplit, listW } = itemsLayout(panelW);
  // 본문이 실제로 놓이는 칸 — 카드 한 장은 2열이어도 485 를 넘지 않으므로 좁은 폭에서만 그보다 작아진다
  const bodyBoxW = Math.min(twoCol ? ROW_MAX_PX : panelW, ROW_MAX_PX) - ROW_CHROME_PX;
  // 아직 재기 전(첫 페인트)에는 그리지 않는다 — 0 으로 재고 그리면 한 프레임 동안 글자가 뭉갠다
  const showBody = panelW > 0 && bodyBoxW >= BODY_MIN_PX;

  // 목록 구성이 바뀌면 관찰을 다시 건다. **순서가 바뀌어도 다시 건다** — 지켜볼 `<li>` 가
  // 자리를 옮기기 때문이다(자국이 있는 pid 는 훅이 걸러 다시 묻지 않는다).
  // 폭이 좁아 본문을 안 그리는 동안에는 관찰 표식 자체가 붙지 않는다 — 그래서 showBody 를
  // 이 키에 섞는다. 안 섞으면 창을 넓혀 본문이 켜져도 관찰이 다시 걸리지 않아 영영 빈 칸이다.
  const rowIdKey = rows.map((r) => r.problem_id).join(',');
  const { listRef, htmlOf, isSeen, failedCount, retryFailed } = useRowBodies(
    getAccessToken,
    showBody ? rowIdKey : '',
  );

  /**
   * 행별 파생값을 **한 번에** 만든다. 행 안에서 그때그때 계산하면 배열이 매 렌더 새로 만들어져
   * memo 로 감싼 행 부품이 전부 다시 그려진다(200행이면 그것만으로 화면이 멎는다).
   */
  const derived = useMemo(() => rows.map((r) => {
    const meta = r.problem_id == null ? null : metaMap[String(r.problem_id)];
    const labels = r.labels || meta?.labels || '';
    return {
      concepts: conceptsOf(r),
      // 상세가 실어 준 값이 먼저 — 담을 때 들고 온 값이 있으면 메타를 기다리지 않고 바로 뜬다
      difficulty: r.difficulty ?? meta?.difficulty ?? null,
      preview: r.preview || meta?.preview || '',
      labels: Array.isArray(labels) ? labels.filter((x) => typeof x === 'string').join(', ') : labels,
    };
  }), [rows, metaMap, conceptsOf]);

  /** 좌측 열이 읽는 통계 한 벌 — 집계는 React 를 모르는 순수 함수가 한다(paperStats.js) */
  const stats = useMemo(
    () => buildPaperStats({ rows, metaMap, dictIndex }),
    [rows, metaMap, dictIndex],
  );

  const save = async () => {
    if (busy) return;
    // title 이 문자열이 아니면(null 포함) 서버가 400 이다(계약 §1-3). 상태는 늘 문자열로 심기지만
    // (seed 의 `p?.title ?? ''`), 상세가 숫자·null 을 주면 그 값이 그대로 앉는다 — 나가기 직전에 굳힌다.
    const t = String(title ?? '').trim();
    const desc = String(description ?? '').trim(); // 같은 이유로 설명도(비문자열이면 .trim() 이 터진다)
    if (t === '') {
      setToast({ type: 'error', text: '제목을 입력해 주세요.' });
      return;
    }
    if (rows.length === 0 && !itemsUnknown) {
      setToast({ type: 'error', text: '문항을 최소 하나는 담아 주세요.' });
      return;
    }
    const items = toPayload(rows);
    // 계약 §1-3 — 같은 problem_id 두 번이면 400. 서버 오류를 보기 전에 막고 어느 번호인지 짚는다
    const dup = duplicateIds(items);
    if (dup.length > 0) {
      setToast({
        type: 'error',
        text: `같은 문제가 두 번 담겨 있습니다(${dup.map((v) => `#${v}`).join(', ')}) — 한쪽을 빼고 다시 저장하세요.`,
      });
      return;
    }
    setBusy('save');
    setToast(null);
    try {
      if (isNew) {
        const d = await createPaper({ title: t, description: desc, items }, getAccessToken);
        if (d === AUTH_REDIRECT) return; // 401 → /signin 이동 중. 성공이라고 말하지 않는다
        const newId = d?.paper_id ?? d?.id;
        if (newId == null) {
          // 만들어지긴 했는데 어디로 가야 할지 모른다 — 말없이 목록으로 떨어뜨리면
          // 사용자는 만들어졌는지조차 알 수 없다.
          setToast({ type: 'error', text: '시험지는 만들어졌지만 번호를 받지 못했습니다 — 목록에서 확인해 주세요.' });
          seedRef.current = { title: t, description: desc, rows };
          return;
        }
        onSaved?.(newId, { created: true });
      } else {
        // 보낸 키만 반영된다 — 문항을 못 읽은 상태면 items 를 빼서 서버 문항을 지키고
        // 제목·설명만 고친다.
        const patch = { title: t, description: desc };
        if (!itemsUnknown) patch.items = items;
        const d = await updatePaper(paper.paper_id ?? paper.id, patch, getAccessToken);
        if (d === AUTH_REDIRECT) return;
        seedRef.current = { title: t, description: desc, rows };
        setToast({ type: 'info', text: '저장했습니다 — 새 판(revision)이 쌓였습니다.' });
        onSaved?.(paper.paper_id ?? paper.id, { created: false });
      }
    } catch (e) {
      // 사유(409/423)를 아는 것은 우리 문구로, 모르는 것(중복 400 등)은 서버 문구로 흘린다
      setToast({ type: 'error', text: describeApiError(e, '저장하지 못했습니다.') });
    } finally {
      setBusy(null);
    }
  };

  const doDelete = async () => {
    if (busy || isNew) return;
    setBusy('delete');
    setToast(null);
    try {
      const d = await deletePaper(paper.paper_id ?? paper.id, getAccessToken);
      if (d === AUTH_REDIRECT) { setBusy(null); return; }
      onDeleted?.();
    } catch (e) {
      // 계약 §10 — 배정이 하나라도 있으면 409 has_assignments{assignment_count} 로 막힌다.
      // describeApiError 가 그 사유를 「이미 N건 배포된 시험지라…」로 갈아 끼운다.
      setToast({ type: 'error', text: describeApiError(e, '삭제하지 못했습니다.') });
      setBusy(null);
    }
  };

  return (
    /* 2열일 때 이 패널은 **자기 높이를 스스로 정한다**(오너 지시 2026-08-28
       「부모요소 크기만큼만 배치하고, 문제 미리보기 부분만 내부 스크롤」).
       위(제목·버튼·경고)와 아래(저장 줄)는 자기 높이만 갖고, 가운데 격자가 남는 높이를 전부
       가진 뒤, 그 안에서 **목록만** 넘치면 스크롤한다. 그래서 바깥 스크롤이 사라지고
       통계 열은 늘 제자리에 선다 — sticky 로 따라오게 하던 것을 걷어 낸 이유다.
       좁은 폭(1열)에서는 고정하지 않는다: 통계가 위에 오므로 높이를 못 박으면 그것만으로
       화면이 다 차고 목록은 아래로 밀려 보이지 않는다. */
    <div
      ref={rootRef}
      className={twoCol ? 'flex min-h-0 flex-col gap-3' : 'space-y-3'}
      style={twoCol && colH > 0 ? { height: colH } : undefined}
    >
      <div className="shrink-0 space-y-2">
        <label className="block">
          <span className="mb-0.5 block text-xs font-medium text-gray-500">제목</span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={busy != null}
            placeholder="예: 2학기 중간 대비 — 이차함수"
            className="w-full rounded-xl border px-3 py-2 text-sm focus:border-primary focus:outline-none disabled:bg-gray-50"
          />
        </label>
        <label className="block">
          <span className="mb-0.5 block text-xs font-medium text-gray-500">설명(선택)</span>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={busy != null}
            rows={2}
            placeholder="이 시험지의 목적·범위 메모"
            className="w-full rounded-xl border px-3 py-2 text-sm focus:border-primary focus:outline-none disabled:bg-gray-50"
          />
        </label>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        {/* 「전체 문항 N개」는 여기서 뺐다 — 좌측 통계 카드가 같은 값을 34px 로 말한다.
            한 화면에서 같은 수를 두 번 적으면 둘이 갈렸을 때 어느 쪽이 참인지 알 수 없다. */}
        <Button size="xs" onClick={() => setPickerOpen(true)} disabled={busy != null}>
          <Plus className="mr-0.5 inline size-3" />
          문항 담기
        </Button>
        {rows.length > 0 && (
          <Button
            size="xs"
            variant="secondary"
            onClick={() => setPreview({
              ids: rows.slice(0, PREVIEW_MAX).map((r) => r.problem_id),
              numbers: rows.slice(0, PREVIEW_MAX).map((_, i) => i + 1),
            })}
            title={rows.length > PREVIEW_MAX
              ? `원본 크기로 훑어봅니다 — 앞 ${PREVIEW_MAX}개까지`
              : '원본 크기로 훑어봅니다'}
          >
            <Eye className="mr-0.5 inline size-3" />
            전체 보기
          </Button>
        )}
        {/* 우측 끝 — 지면을 보며 담는 3열 창으로 넘어간다. 이 행에 두는 이유는 여기가
            「문항을 어떻게 다룰까」를 고르는 자리이기 때문이고, 따로 안내 띠를 두르면
            같은 말을 두 번 하게 된다. */}
        {onOpenCompose && (
          <Button size="xs" variant="secondary" className="ml-auto" onClick={onOpenCompose} disabled={busy != null}>
            시험지 편집
          </Button>
        )}
      </div>

      {itemsUnknown && (
        <p role="alert" className="shrink-0 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          서버가 준 문항 목록을 읽지 못했습니다 — 화면이 비어 보여도 시험지에는 문항이 남아 있습니다.
          안전을 위해 저장할 때 문항은 건드리지 않고 제목·설명만 보냅니다.
        </p>
      )}
      {deletedCount > 0 && (
        /* 계약 §1-3 — 이 시험지에 삭제된 문항이 있다. 저장(POST/PATCH)은 되지만
           배포·복사·되돌리기는 409 로 막힌다. 그 셋을 눌러 보기 전에 여기서 먼저 말한다.
           띠 모양은 바로 위 itemsUnknown 띠와 같은 것을 쓴다 — 같은 층위의 경고다. */
        <p role="alert" className="shrink-0 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          원본이 삭제된 문항이 {deletedCount}개 있습니다 — 그대로 두면 <b>배포·복사·되돌리기가 막힙니다.</b>
          아래 목록에서 표시된 행을 빼고 저장해 주세요.
        </p>
      )}
      {/* ══ 2열 — 좌: 이 시험지가 무엇으로 이뤄졌는가 · 우: 문항 하나하나 ══════════
          좌우로 가른 것은 두 물음이 서로 다르기 때문이다. 통계는 통째로 보는 값이라
          스크롤을 따라와야 하고(sticky), 목록은 내려가며 읽는 값이다.
          격자 밖에 둔 것들(제목·버튼 줄·경고 띠·발치 액션)은 두 열을 가로지르는 것들이다.

          클래스 문자열을 이어 붙여 만들지 않는다 — Tailwind JIT 는 소스에 그대로 있는
          문자열만 잡는다. 그래서 두 벌을 나란히 적는다.
          오른쪽 칸 폭은 **판정 결과(listW)를 style 로** 받는다 — 카드 1열 485 · 2열 978. 클래스에 숫자를
          적으면 폭이 판정 파일과 여기 두 벌이 된다. gap-4 는 paperItemsLayout 의 PANEL_GAP_PX(16)다 —
          한쪽만 바꾸면 문턱 셈이 어긋난다. */}
      <div
        className={`grid gap-4 ${twoCol ? 'min-h-0 flex-1' : 'grid-cols-1'}`}
        style={twoCol ? { gridTemplateColumns: `minmax(0,1fr) ${listW}px` } : undefined}
      >
        {/* 격자 항목은 기본 stretch 라 이 칸은 이미 행 높이만큼이다 — 통계 한 장이 h-full 로
            그 높이를 그대로 쓰고, 안의 구획들이 남는 높이를 나눠 가진다. 스크롤은 없다. */}
        <aside className="min-h-0 min-w-0">
          <PaperStatsPanel
            stats={stats}
            itemsUnknown={itemsUnknown}
            onRetryMeta={retryMeta}
            onConceptClick={openConcept}
            /* 넓을 때 「분포 | 개념 목록」 두 칸 — 카드가 2열로 선 뒤에만 참이다(paperItemsLayout) */
            split={statsSplit}
          />
        </aside>

        {/* 오른쪽 칸만 자기 안에서 스크롤한다(오너 지시 2026-08-28). 바깥이 스크롤하면
            통계 열이 함께 밀려 올라가 「통째로 보는 자리」가 사라진다. */}
        <div className="flex min-h-0 min-w-0 flex-col">
          {/* 본문 묶음이 통째로 실패했을 때만 뜬다. 「응답은 왔는데 그 문항만 없는 경우」는
              여기 오지 않는다 — 그쪽은 문항이 실제로 없는 것이라 다시 물어도 같은 답이다. */}
          {failedCount > 0 && (
            <p
              role="alert"
              style={{ maxWidth: listW }}
              className="mb-2 shrink-0 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            >
              본문 {failedCount}개를 불러오지 못했습니다 — 그 행은 요약만 보입니다.{' '}
              <button type="button" onClick={retryFailed} className="underline">다시 시도</button>
            </p>
          )}

          {/* pr-1 은 스크롤바 몫이다 — 없으면 막대가 행 오른쪽 테두리를 덮는다 */}
          {/* scrollbar-gutter 는 오버플로 유무와 무관하게 본문 폭을 고정한다.
              폭 → ProblemBodyPreview 배율·높이 → 오버플로 → 스크롤바 유무가 서로를
              바꾸는 임계 높이에서 두 레이아웃을 무한히 오가는 것을 막는다. */}
          <div className="min-h-0 flex-1 overflow-y-auto pr-1 [scrollbar-gutter:stable]">
          {rows.length === 0 ? (
            <p
              style={{ maxWidth: listW }}
              className="rounded-xl border border-dashed border-gray-200 p-6 text-center text-sm text-gray-400"
            >
              담긴 문항이 없습니다 — [문항 담기]로 개념·라벨로 찾아 담으세요.
            </p>
          ) : (
            /* ref 는 본문 훅의 관찰 컨테이너다 — 이 안의 [data-body-pid] 를 지켜보다가
               화면에 든 것만 20개씩 받아 온다. 행 자체는 **전부** 그린다: 잘라 그리면
               순번·[위로]·[빼기]가 창 상대 인덱스가 되어 3번을 올렸는데 13번이 올라간다.
               카드 2열은 **행 우선**(01 02 / 03 04)이다 — 지면처럼 왼쪽 단부터 채우는 순서는 끝이 없는
               스크롤 목록에서 성립하지 않는다. 한 행의 두 카드는 격자 기본값(stretch)으로 높은 쪽에
               맞춰 늘어나 아래 선이 맞는다(오너 결정 2026-09-15). 관찰은 행마다 걸려 배치와 무관하다. */
            <ul
              ref={listRef}
              style={{ maxWidth: listW }}
              className={listCols === 2 ? 'grid grid-cols-2 gap-x-2 gap-y-1' : 'space-y-1'}
            >
              {rows.map((r, idx) => (
                /* key 에 인덱스를 섞으면 위·아래로 옮길 때마다 두 행이 통째로 새로 그려져,
                   방금 누른 버튼이 사라지고 초점이 body 로 떨어진다(연속으로 못 올린다). */
                <PaperItemPreviewRow
                  key={r.problem_id}
                  problemId={r.problem_id}
                  idx={idx}
                  concepts={derived[idx].concepts}
                  difficulty={derived[idx].difficulty}
                  preview={derived[idx].preview}
                  labels={derived[idx].labels}
                  isDeleted={r.isDeleted}
                  showBody={showBody}
                  bodyHtml={htmlOf(r.problem_id)}
                  bodySeen={isSeen(r.problem_id)}
                  onConceptClick={openConcept}
                />
              ))}
            </ul>
          )}
          </div>
        </div>
      </div>

      {/* 발치 액션 줄 — 좌: 삭제(되돌릴 수 없는 것) · 가운데: 미저장 알림 · 우: 되돌리기 → 저장.
          저장이 가장 오른쪽이다(오너 지시). 삭제를 반대쪽 끝에 떼어 둔 것은 손이 미끄러져도
          저장 옆에서 눌리지 않게 하려는 것이고, 그래서 색도 붉게 세운다. */}
      <div className="flex shrink-0 flex-wrap items-center gap-2 border-t pt-3">
        {/* 좌 — 시험지 삭제 */}
        {!isNew && (
          confirmDelete ? (
            <span className="flex items-center gap-1.5 text-xs text-danger">
              삭제할까요?
              <Button size="xs" variant="danger" onClick={doDelete} disabled={busy != null}>
                {busy === 'delete' ? '삭제 중…' : '삭제'}
              </Button>
              <Button size="xs" variant="secondary" onClick={() => setConfirmDelete(false)} disabled={busy != null}>취소</Button>
            </span>
          ) : (
            <Button
              size="sm"
              variant="danger"
              onClick={() => setConfirmDelete(true)}
              disabled={busy != null}
            >
              <Trash2 className="mr-1 inline size-3.5" />
              시험지 삭제
            </Button>
          )
        )}

        {/* 가운데 — 미저장 알림. 남는 폭을 통째로 먹어 가운데에 선다.
            문구가 없을 때도 이 칸은 남아, 좌우 버튼이 양 끝으로 벌어진 자리를 지킨다. */}
        {/* 가운데 — 알림 두 줄. 위는 방금 한 일의 결과(담기·저장 실패 등), 아래는 지속 상태.
            감싸는 칸을 늘 두는 이유가 둘이다: ① 알림이 없을 때도 [시험지 삭제]와 [저장]이
            붙지 않는다 ② toast 는 비어 있을 때 sr-only 인데, 그 클래스는 position:absolute 라
            자기가 flex 자식이면 flex-1 이 죽는다. 한 겹 감싸면 그 문제가 사라진다. */}
        <div className="min-w-0 flex-1 text-center">
          <p
            role={toast?.type === 'error' ? 'alert' : 'status'}
            aria-live={toast?.type === 'error' ? 'assertive' : 'polite'}
            className={`text-xs ${toast ? (toast.type === 'error' ? 'text-danger' : 'text-gray-600') : 'sr-only'}`}
          >
            {toast?.text || ''}
          </p>
          {dirty && (
            <p className="text-[11px] text-amber-600">
              저장하지 않은 변경이 있습니다{isNew ? ' — [시험지 만들기]를 눌러야 서버에 생깁니다.' : '.'}
            </p>
          )}
        </div>

        {/* 우 — 되돌리기 다음에 저장 */}
        {dirty && !isNew && (
          confirmRevert ? (
            <span className="flex items-center gap-1.5 text-xs text-gray-600">
              편집을 버릴까요?
              <Button size="xs" onClick={() => { seed(paper); setConfirmRevert(false); }} disabled={busy != null}>버리기</Button>
              <Button size="xs" variant="secondary" onClick={() => setConfirmRevert(false)} disabled={busy != null}>취소</Button>
            </span>
          ) : (
            <Button size="sm" variant="secondary" onClick={() => setConfirmRevert(true)} disabled={busy != null}>
              <RotateCcw className="mr-1 inline size-3.5" />
              되돌리기
            </Button>
          )
        )}
        <Button size="sm" onClick={save} disabled={busy != null || (!isNew && !dirty)}>
          <Save className="mr-1 inline size-3.5" />
          {busy === 'save' ? '저장 중…' : isNew ? '시험지 만들기' : '저장'}
        </Button>
      </div>


      <ProblemPickerModal
        isOpen={pickerOpen}
        existingIds={rows.map((r) => r.problem_id)}
        onClose={() => setPickerOpen(false)}
        onAdd={addProblems}
      />

      {conceptDetail && (
        <MathceptDetailModal
          isOpen
          onClose={() => setConceptDetail(null)}
          /* data 를 주면 모달이 자체 조회(v1)를 건너뛴다 — 이 화면이 v2 전용인 근거다 */
          data={conceptDetail.data}
          loading={conceptDetail.loading}
          handleAuth={false}
        />
      )}

      {preview && (
        <Suspense fallback={null}>
          <ProblemPreviewV2
            isOpen
            onClose={() => setPreview(null)}
            problemIds={preview.ids}
            numbers={preview.numbers}
          />
        </Suspense>
      )}
    </div>
  );
}

export default PaperItemsEditor;
